import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AddReviewForm } from "@/components/admin/AddReviewForm";
import { AdminReviewLifecycleControls } from "@/components/admin/AdminReviewLifecycleControls";
import type {
  ActionStatus,
  TakedownAttempt,
} from "@/components/admin/AdminReviewLifecycleControls";
import { FlagToggle } from "@/components/admin/FlagToggle";
import { CopyReviewUrlButton } from "@/components/customer/CopyReviewUrlButton";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type MonitoringCadence = "default" | "daily" | "weekly";
type Violation = { type: string; confidence: number; evidence?: string };

type Product = {
  id: string;
  asin: string;
  productTitle: string | null;
  catalogSource: string;
  monitoringEnabled: boolean;
  monitoringCadence: MonitoringCadence;
  effectiveCadence: "daily" | "weekly";
  addedAt: string;
  lastScrapedAt: string | null;
  lastScheduledAt: string | null;
  lastCompletedAt: string | null;
  nextRunAt: string | null;
  isDue: boolean;
  amazonUrl: string;
};

type ProductReview = {
  id: string;
  auditRequest: { id: string; createdAt: string; status: string };
  amazonReviewId: string;
  reviewUrl: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  reviewDate: string | null;
  createdAt: string;
  classification: {
    id: string;
    flaggedForTakedown: boolean;
    violations: Violation[];
    rationale: string | null;
    takedownRequest: string | null;
    actionStatus: ActionStatus;
    submittedAt: string | null;
    removedAt: string | null;
    chargeStatus: string;
    takedownAttempts: TakedownAttempt[];
  } | null;
};

type ProductReviewsResponse = {
  product: Product;
  brand: { id: string; name: string };
  customer: { id: string; email: string; organizationName: string };
  summary: {
    reviewCount: number;
    flaggedCount: number;
  };
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
  reviews: ProductReview[];
};

const statusOptions = [
  { value: "all", label: "All reviews" },
  { value: "flagged", label: "Flagged" },
  { value: "clean", label: "Clean" },
  { value: "submitted", label: "Submitted" },
  { value: "removed", label: "Removed" },
  { value: "rejected", label: "Rejected" },
  { value: "watching", label: "Watching" },
  { value: "none", label: "No action" },
  { value: "unclassified", label: "Unclassified" },
];

function formatDate(value: string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "neutral" | "green" | "amber" | "red" | "blue";
}) {
  const cls = {
    neutral:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    green:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
    amber:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
    red:
      "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
    blue:
      "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  }[tone];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {children}
    </span>
  );
}

function actionTone(status: ActionStatus | null | undefined) {
  if (status === "removed") return "green";
  if (status === "submitted") return "blue";
  if (status === "rejected") return "red";
  if (status === "watching") return "amber";
  return "neutral";
}

function reviewStatus(review: ProductReview) {
  if (!review.classification) {
    return { label: "Unclassified", tone: "neutral" as const };
  }
  if (review.classification.flaggedForTakedown) {
    return { label: "Flagged", tone: "amber" as const };
  }
  return { label: "Clean", tone: "green" as const };
}

function ProductReviewRow({
  review,
  productAsin,
  onChange,
}: {
  review: ProductReview;
  productAsin: string;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const statusInfo = reviewStatus(review);
  const actionStatus = review.classification?.actionStatus ?? null;

  async function onDelete() {
    if (!window.confirm("Delete this review? This also removes its AI verdict.")) {
      return;
    }
    setDeleting(true);
    const res = await api(`/admin/reviews/${review.id}`, { method: "DELETE" });
    setDeleting(false);
    if (!res.ok) {
      alert(res.error ?? "Delete failed");
      return;
    }
    onChange();
  }

  return (
    <article className="px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{review.rating}/5</span>
            <Pill tone={statusInfo.tone}>{statusInfo.label}</Pill>
            <Pill tone={actionTone(actionStatus)}>
              {actionStatus ?? "none"}
            </Pill>
            {review.verifiedPurchase && (
              <span className="text-xs text-muted-foreground">Verified</span>
            )}
            <span className="text-xs text-muted-foreground">
              {formatDate(review.reviewDate)}
            </span>
          </div>
          <h2 className="mt-2 text-sm font-semibold">
            {review.title || "Untitled review"}
          </h2>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 text-xs">
          <Link
            to={`/admin/audits/${review.auditRequest.id}`}
            className="font-mono text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {review.auditRequest.id.slice(0, 10)}
          </Link>
          {review.reviewUrl && (
            <>
              <a
                href={review.reviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                Amazon <ExternalLink className="h-3 w-3" />
              </a>
              <CopyReviewUrlButton url={review.reviewUrl} />
            </>
          )}
        </div>
      </div>

      <div className="mt-3">
        {editing ? (
          <AddReviewForm
            auditId={review.auditRequest.id}
            asins={[productAsin]}
            review={{
              id: review.id,
              asin: productAsin,
              rating: review.rating,
              title: review.title,
              body: review.body,
              reviewerName: review.reviewerName,
              reviewDate: review.reviewDate,
              verifiedPurchase: review.verifiedPurchase,
            }}
            onChange={() => {
              setEditing(false);
              onChange();
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            <p className="text-sm leading-6 text-foreground">{review.body}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onDelete}
                disabled={deleting}
                className="text-destructive hover:text-destructive/80"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </>
        )}
      </div>

      {review.classification ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <FlagToggle
              reviewId={review.id}
              initialFlagged={review.classification.flaggedForTakedown}
              onChange={onChange}
            />
          </div>
          {review.classification.rationale && (
            <p className="rounded-lg bg-muted/50 p-3 text-xs leading-5 text-muted-foreground">
              {review.classification.rationale}
            </p>
          )}
          <AdminReviewLifecycleControls
            reviewId={review.id}
            actionStatus={review.classification.actionStatus}
            attempts={review.classification.takedownAttempts}
            onChange={onChange}
          />
        </div>
      ) : (
        <p className="mt-3 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
          Not classified yet. Open the source audit to classify this review.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Scraped {formatDateTime(review.createdAt)}</span>
        <span>Review ID {review.amazonReviewId}</span>
        <span>Reviewer {review.reviewerName || "-"}</span>
      </div>
    </article>
  );
}

function ProductReviewsInner() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ProductReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "all";
  const rating = searchParams.get("rating") ?? "all";
  const verified = searchParams.get("verified") ?? "all";
  const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
  const requestParams = new URLSearchParams(searchParams);
  requestParams.set("page", String(page));
  requestParams.set("pageSize", "25");
  const requestQuery = requestParams.toString();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<ProductReviewsResponse>(`/admin/products/${id}/reviews?${requestQuery}`).then(
      (res) => {
        if (cancelled) return;
        setLoading(false);
        if (!res.ok || !res.data) {
          setData(null);
          setError(res.error ?? "Could not load product reviews.");
          return;
        }
        setData(res.data);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [id, requestQuery, reloadKey]);

  function reload() {
    setReloadKey((current) => current + 1);
  }

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setSearchParams(next);
  }

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next);
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading reviews...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <Link to="/admin/customers" className="text-sm text-muted-foreground hover:text-foreground">
          Back to customers
        </Link>
        <p className="mt-4 flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" /> {error}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { product, brand, customer, summary, reviews } = data;
  const from = data.total === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
  const to = Math.min(data.page * data.pageSize, data.total);

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={`/admin/products/${product.id}`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Back to product
        </Link>
        <section className="mt-4 rounded-2xl border border-border bg-card p-5 text-card-foreground">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {customer.organizationName} / {brand.name}
              </div>
              <h1 className="mt-1 font-mono text-2xl font-semibold tracking-tight">
                {product.asin} reviews
              </h1>
              {product.productTitle && (
                <p className="mt-1 max-w-4xl truncate text-sm text-muted-foreground">
                  {product.productTitle}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Pill tone={product.monitoringEnabled ? "green" : "neutral"}>
                  {product.monitoringEnabled ? "Monitored" : "Paused"}
                </Pill>
                <Pill tone="neutral">{summary.reviewCount} saved reviews</Pill>
                <Pill tone={summary.flaggedCount > 0 ? "amber" : "neutral"}>
                  {summary.flaggedCount} flagged
                </Pill>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/admin/customers/${customer.id}`}
                className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
              >
                Customer
              </Link>
              <a
                href={product.amazonUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
              >
                Amazon <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-border bg-card p-4 text-card-foreground">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_180px_140px_170px]">
          <label className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(event) => setFilter("q", event.target.value)}
              placeholder="Search review text, title, reviewer, or review ID"
              aria-label="Search reviews"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
          <select
            value={status}
            onChange={(event) => setFilter("status", event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground dark:bg-input/30 dark:scheme-dark"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={rating}
            onChange={(event) => setFilter("rating", event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground dark:bg-input/30 dark:scheme-dark"
          >
            <option value="all">Any rating</option>
            <option value="1">1 star</option>
            <option value="2">2 stars</option>
            <option value="3">3 stars</option>
            <option value="4">4 stars</option>
            <option value="5">5 stars</option>
          </select>
          <select
            value={verified}
            onChange={(event) => setFilter("verified", event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground dark:bg-input/30 dark:scheme-dark"
          >
            <option value="all">Any purchase</option>
            <option value="verified">Verified only</option>
            <option value="unverified">Unverified only</option>
          </select>
        </div>
      </section>

      {error && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" /> {error}
        </p>
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-card text-card-foreground">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 text-sm text-muted-foreground">
          <span>
            Showing {from}-{to} of {data.total} matching reviews
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={data.page <= 1 || loading}
              onClick={() => goToPage(data.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <span className="text-xs">
              Page {data.page} of {data.pageCount}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={data.page >= data.pageCount || loading}
              onClick={() => goToPage(data.page + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Updating reviews...
          </div>
        )}

        <div className="divide-y divide-border">
          {reviews.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              No reviews match these filters.
            </div>
          )}
          {reviews.map((review) => (
            <ProductReviewRow
              key={review.id}
              review={review}
              productAsin={product.asin}
              onChange={reload}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

export default function AdminProductReviews() {
  return (
    <AdminGate>
      <AdminLayout>
        <ProductReviewsInner />
      </AdminLayout>
    </AdminGate>
  );
}
